import React, { useMemo, useState } from "react";
import { Table, Card, CardBody, Button } from "reactstrap";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Loader from "../../Loader";
import {
  faChevronLeft,
  faChevronRight,
  faAngleDoubleLeft,
  faAngleDoubleRight,
} from "@fortawesome/free-solid-svg-icons";

// Move legend items outside to avoid re-definition on every render
const LEGEND_ITEMS = [
  { key: "sequenceShipDate", color: "#1575d4", label: "Sequence Ship" },
  { key: "transmittalNeedDate", color: "#ee5ef8", label: "Transmittal Need" },
  {
    key: "processedKittedNeedDate",
    color: "#316333",
    label: "Processed / Kitted Need",
  },
  { key: "fabCompleteDate", color: "#c67b1c", label: "Fab Complete" },
  { key: "backFromOpDate", color: "#b80000", label: "Back from OP" },
];

// Internal helper for navigation buttons
const NavButton = ({ icon, onClick, title }) => (
  <Button
    size="sm"
    color="link"
    className="text-secondary p-1 box-shadow-none"
    onClick={onClick}
    title={title}
  >
    <FontAwesomeIcon icon={icon} />
  </Button>
);

const JobSchedulingCalendar = ({ jobs = [], isLoading = false }) => {
  const [currentDate, setCurrentDate] = useState(() =>
    moment().startOf("month")
  );

  // Memoize date range and calculations
  const { days, months, today } = useMemo(() => {
    const start = currentDate.clone().startOf("month");
    const end = currentDate.clone().endOf("month");
    const todayDate = moment().startOf("day");

    const daysArr = [];
    let curr = moment(start);
    while (curr <= end) {
      daysArr.push(moment(curr));
      curr.add(1, "days");
    }

    const monthsArr = [];
    let currentMonth = null;
    daysArr.forEach((day) => {
      const monthName = day.format("MMMM");
      if (!currentMonth || currentMonth.name !== monthName) {
        currentMonth = { name: monthName, count: 1 };
        monthsArr.push(currentMonth);
      } else {
        currentMonth.count++;
      }
    });

    return { days: daysArr, months: monthsArr, today: todayDate };
  }, [currentDate]);

  // Consolidated navigation handler
  const navigateDate = (amount, unit) => {
    if (amount === 0) {
      setCurrentDate(moment().startOf("month"));
    } else {
      setCurrentDate((prev) => prev.clone().add(amount, unit));
    }
  };

  const getEventsForDate = (job, date) => {
    const scheduling = job.scheduling;
    if (!scheduling) return [];

    const formattedDate = date.format("YYYY-MM-DD");
    return LEGEND_ITEMS.filter((item) => {
      const jobDate = scheduling[item.key];
      return jobDate && moment(jobDate).format("YYYY-MM-DD") === formattedDate;
    });
  };

  return (
    <div className="scheduling-calendar-container">
      <Card className="shadow-none h-100 mb-0">
        <CardBody className="d-flex flex-column p-3">
          {/* Header & Legend */}
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
            <div className="d-flex flex-wrap p-0 bg-white">
              {LEGEND_ITEMS.map((item, idx) => (
                <div key={idx} className="d-flex align-items-center mr-4 mb-2">
                  <span
                    className="mr-2 rounded-circle legend-indicator"
                    style={{
                      border: `1px solid ${item.color}`,
                      backgroundColor: `${item.color}20`,
                    }}
                  />
                  <small className="text-muted font-weight-bold">
                    {item.label}
                  </small>
                </div>
              ))}
            </div>

            <div className="d-flex align-items-center mb-2">
              <NavButton
                icon={faAngleDoubleLeft}
                onClick={() => navigateDate(-1, "year")}
                title="Prev Year"
              />
              <NavButton
                icon={faChevronLeft}
                onClick={() => navigateDate(-1, "month")}
                title="Prev Month"
              />
              <Button
                size="sm"
                color="outline-primary"
                className="mx-2 px-3 box-shadow-none"
                onClick={() => navigateDate(0)}
              >
                Today
              </Button>
              <NavButton
                icon={faChevronRight}
                onClick={() => navigateDate(1, "month")}
                title="Next Month"
              />
              <NavButton
                icon={faAngleDoubleRight}
                onClick={() => navigateDate(1, "year")}
                title="Next Year"
              />
            </div>
          </div>

          <div className="table-responsive flex-grow-1">
            <Table size="sm" className="mb-0 text-center scheduling-table">
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    colSpan={3}
                    className="align-middle bg-light sticky-left"
                  >
                    Job #
                  </th>
                  {months.map((m, idx) => (
                    <th
                      key={idx}
                      colSpan={m.count}
                      className="bg-light text-left pl-3 py-2 border-bottom"
                    >
                      <h6 className="mb-0 font-weight-bold">
                        {m.name} {currentDate.format("YYYY")}
                      </h6>
                    </th>
                  ))}
                </tr>
                <tr>
                  {days.map((day, idx) => {
                    const isToday = day.isSame(today, "day");
                    const isWeekend = day.day() === 0 || day.day() === 6;
                    return (
                      <th
                        key={idx}
                        className={`small font-weight-normal py-2 day-cell ${
                          isToday
                            ? "bg-primary text-white"
                            : isWeekend
                            ? "bg-soft-gray"
                            : ""
                        }`}
                      >
                        <div className="text-uppercase mb-1 day-header-abbr">
                          {day.format("ddd")}
                        </div>
                        <div className="font-weight-bold h6 mb-0">
                          {day.format("D")}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={days.length + 3} className="py-5">
                      <Loader size="sm" />
                    </td>
                  </tr>
                ) : jobs.length > 0 ? (
                  jobs.map((job, jobIdx) => (
                    <tr key={jobIdx}>
                      <td
                        colSpan={3}
                        className="text-left sticky-left bg-white align-middle px-2"
                      >
                        {job.jobNumber || job.id}
                      </td>
                      {days.map((day, dayIdx) => {
                        const events = getEventsForDate(job, day);
                        const isToday = day.isSame(today, "day");
                        const isWeekend = day.day() === 0 || day.day() === 6;

                        return (
                          <td
                            key={dayIdx}
                            className={`p-0 position-relative align-middle day-cell ${
                              isToday
                                ? "bg-primary"
                                : isWeekend
                                ? "bg-soft-gray"
                                : ""
                            }`}
                          >
                            <div className="d-flex h-100 w-100 py-1">
                              {events.map((event, eventIdx) => (
                                <div
                                  key={eventIdx}
                                  className="event-indicator bg-white rounded-lg"
                                >
                                  <div
                                    className="event-color-box rounded-lg"
                                    style={{
                                      border: `1px solid ${event.color}`,
                                      backgroundColor: `${event.color}20`,
                                      color: event.color,
                                      boxShadow: `inset 0 0 4px ${event.color}30`,
                                    }}
                                    title={event.label}
                                  />
                                </div>
                              ))}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={days.length + 3} className="py-5 text-muted">
                      No jobs to display in the current period.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default JobSchedulingCalendar;
